import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import { useState } from "react";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/input";
import { useSession } from "@/hooks/useSession";
import { api, type PageTemplate } from "@/lib/api";
import { fmtTime } from "@/lib/utils";

export default function Pages() {
  const qc = useQueryClient();
  const { user } = useSession();
  const canOperate = user?.role === "owner" || user?.role === "operator";
  const [kind, setKind] = useState<"block" | "maintenance">("block");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState("");
  const [err, setErr] = useState("");

  const { data } = useQuery({
    queryKey: ["page-templates"],
    queryFn: () => api.get<{ templates: PageTemplate[] }>("/api/pages/templates")
      .catch((e: Error) => ({ moduleOff: e.message })),
    refetchInterval: 15_000,
  });

  const create = useMutation({
    mutationFn: () => api.post("/api/pages/templates", { kind, body, activate: true }),
    onSuccess: () => { setBody(""); setErr(""); qc.invalidateQueries({ queryKey: ["page-templates"] }); },
    onError: (e) => setErr(e.message),
  });
  const previewMut = useMutation({
    mutationFn: () => api.post<{ html: string }>("/api/pages/preview", {
      body, vars: { app_name: "Example App", host: "app.example.com",
        path: "/demo?x=1", reason: "request denied by the web application firewall",
        request_id: "demo-req-001", support_email: "ops@example.com",
        product: "NXShield" },
    }),
    onSuccess: (d) => { setPreview(d.html); setErr(""); },
    onError: (e) => { setPreview(""); setErr(e.message); },
  });
  const activate = useMutation({
    mutationFn: (id: string) => api.post(`/api/pages/templates/${id}/activate`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["page-templates"] }),
    onError: (e) => setErr(e.message),
  });

  if (data && "moduleOff" in data) {
    return (
      <div className="space-y-5">
        <PageHeader title="Custom pages" description="" />
        <EmptyState icon={FileText} title="nx.pages is not enabled"
          hint={`Enable the module on the Modules page — API said: ${data.moduleOff}`} />
      </div>
    );
  }
  const templates = (data as { templates?: PageTemplate[] })?.templates ?? [];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Custom pages"
        description="nx.pages — original branded templates rendered server-side with escaped {{variables}}. Executable content is rejected; rollback activates an older version without rewriting history."
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Label className="w-16">kind</Label>
            <select className="rounded border bg-background px-2 py-1 text-sm"
              value={kind} onChange={(e) => setKind(e.target.value as "block" | "maintenance")}>
              <option value="block">block (403)</option>
              <option value="maintenance">maintenance (503)</option>
            </select>
          </div>
          <textarea
            className="h-48 w-full rounded border bg-background p-2 font-mono text-xs"
            placeholder={"<html>… {{app_name}} {{reason}} {{request_id}} …</html>\nallowed vars: app_name host path reason request_id support_email product"}
            value={body} onChange={(e) => setBody(e.target.value)} />
          {canOperate && (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => previewMut.mutate()}
                disabled={!body || previewMut.isPending}>Preview</Button>
              <Button size="sm" onClick={() => create.mutate()}
                disabled={!body || create.isPending}>Save new version (active)</Button>
            </div>
          )}
          {err && <p className="text-sm text-destructive">{err}</p>}
        </Card>
        <Card className="p-4">
          <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            preview — rendered server-side, escaped
          </div>
          {preview
            ? <div className="overflow-hidden rounded border" dangerouslySetInnerHTML={{ __html: preview }} />
            : <p className="text-sm text-muted-foreground">Preview renders the exact bytes the edge will serve.</p>}
        </Card>
      </div>

      <Card>
        <div className="border-b px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          template versions
        </div>
        <div className="divide-y">
          {templates.map((t) => (
            <div key={t.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="outline" className="font-mono">{t.kind}</Badge>
                <span className="font-mono text-xs">v{t.version}</span>
                <Badge variant="secondary" className="text-[10px]">
                  {t.app_id ? "app-scoped" : "global"}
                </Badge>
                {t.active && <Badge variant="success">active</Badge>}
                <span className="text-xs text-muted-foreground">
                  vars: {t.vars.join(", ") || "none"}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>{t.created_by} · {fmtTime(t.created_at)}</span>
                {canOperate && !t.active && (
                  <Button size="sm" variant="ghost" onClick={() => activate.mutate(t.id)}>
                    roll back here
                  </Button>
                )}
              </div>
            </div>
          ))}
          {!templates.length && (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              No custom templates yet — the built-in branded defaults serve until you save a version.
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
